import { useState, useEffect } from 'react';
import {
  ArrowLeft, Plus, Search, Package, ChevronRight,
  AlertTriangle,
} from 'lucide-react';

const STAGE_LABELS = {
  confirmed: { label: 'Confirmed', bg: 'bg-blue-100', text: 'text-blue-700' },
  fabric_sourcing: { label: 'Fabric Sourcing', bg: 'bg-purple-100', text: 'text-purple-700' },
  at_karigar: { label: 'At Karigar', bg: 'bg-amber-100', text: 'text-amber-700' },
  stitching_done: { label: 'Stitching Done', bg: 'bg-cyan-100', text: 'text-cyan-700' },
  fitting_qc: { label: 'Fitting / QC', bg: 'bg-indigo-100', text: 'text-indigo-700' },
  ready: { label: 'Ready', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  delivered: { label: 'Delivered', bg: 'bg-gray-100', text: 'text-gray-600' },
  cancelled: { label: 'Cancelled', bg: 'bg-red-100', text: 'text-red-600' },
};

const OrderList = ({ authAxios, source, title, eventId, onBack, onOrderClick, onNewOrder }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = {};
      if (source) params.source = source;
      if (eventId) params.event_id = eventId;
      if (stageFilter) params.stage = stageFilter;
      if (paymentFilter) params.payment_status = paymentFilter;
      if (search) params.search = search;
      const res = await authAxios.get('/api/order-book/orders/', { params });
      setOrders(res.data);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, [source, eventId, stageFilter, paymentFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchOrders();
  };

  const isOverdue = (order) => {
    if (!order.expected_delivery_date || order.current_stage === 'delivered' || order.current_stage === 'cancelled') return false;
    return new Date(order.expected_delivery_date) < new Date();
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={onBack} className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-xl active:scale-95 transition-all">
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900 tracking-tight">{title}</h2>
          <p className="text-gray-500 text-sm font-medium">{orders.length} orders</p>
        </div>
        <button onClick={onNewOrder}
          className="flex items-center gap-2 px-4 py-3 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 active:scale-95 transition-all shadow-sm">
          <Plus className="w-4 h-4" /> Add Order
        </button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-3">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, order number..."
            className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent shadow-sm" />
        </div>
      </form>

      {/* Filter Chips */}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1 -mx-1 px-1">
        <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
          className="px-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900 shadow-sm min-w-fit">
          <option value="">All Stages</option>
          {Object.entries(STAGE_LABELS).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>
        <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}
          className="px-3.5 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900 shadow-sm min-w-fit">
          <option value="">All Payments</option>
          <option value="unpaid">Unpaid</option>
          <option value="partial">Partial</option>
          <option value="paid">Paid</option>
        </select>
      </div>

      {/* Order Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-10 h-10 border-[3px] border-gray-200 border-t-gray-800 rounded-full animate-spin" />
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-gray-800 font-semibold text-lg">No orders yet</p>
          <p className="text-gray-500 text-sm mt-1">Add your first order to start tracking</p>
          <button onClick={onNewOrder}
            className="mt-5 px-6 py-3 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 active:scale-95 transition-all">
            Add Order
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => {
            const stage = STAGE_LABELS[order.current_stage] || STAGE_LABELS.confirmed;
            const overdue = isOverdue(order);
            const productNames = (order.products || []).map(p => p.name || p.product_name || 'Item').join(', ');

            return (
              <button key={order.id} onClick={() => onOrderClick(order)}
                className={`w-full bg-white rounded-2xl border p-5 text-left hover:shadow-md active:scale-[0.98] transition-all shadow-sm ${
                  overdue ? 'border-red-200' : 'border-gray-100'
                }`}>
                {/* Customer + Amount */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0 mr-3">
                    <div className="flex items-center gap-2">
                      <span className="text-base font-bold text-gray-900 truncate">{order.customer_name}</span>
                      {overdue && <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                    </div>
                    <p className="text-sm text-gray-500 truncate mt-0.5">{productNames || 'No products listed'}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-base font-bold text-gray-900">
                      ₹{Number(order.total_amount || 0).toLocaleString('en-IN')}
                    </div>
                  </div>
                </div>

                {/* Tags row */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${stage.bg} ${stage.text}`}>
                      {stage.label}
                    </span>
                    {order.payment_status === 'paid' && (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">Paid</span>
                    )}
                    {order.payment_status === 'partial' && (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">
                        ₹{Number(order.amount_paid || 0).toLocaleString('en-IN')} paid
                      </span>
                    )}
                    {order.payment_status === 'unpaid' && (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-600">Unpaid</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {order.expected_delivery_date && (
                      <span className={`text-xs font-medium ${overdue ? 'text-red-500' : 'text-gray-400'}`}>
                        {overdue ? 'Overdue' : 'Due'}{' '}
                        {new Date(order.expected_delivery_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrderList;
